import { FC, Fragment, ReactNode } from 'react';

import { useInfoConfig } from '../Info.context';
import type { FieldRenderOptions, InfoField } from './Fields.types';
import { PlainFieldRenderer } from './PlainFieldRenderer';
import type { FieldOverride } from './utils';

interface Props {
	fields: InfoField[];
	overrides: Map<string, FieldOverride>;
	extraFields?: InfoField[];
	renderField?: (field: InfoField, options: FieldRenderOptions) => ReactNode;
}

const renderPlainField: NonNullable<Props['renderField']> = (field, options) => (
	<PlainFieldRenderer field={field} {...options} />
);

export const AutoRenderedFields: FC<Props> = ({
	fields,
	overrides,
	extraFields = [],
	renderField = renderPlainField,
}) => {
	const { currency, locale, formatValue, getIcon, workspace } = useInfoConfig();
	const options: FieldRenderOptions = {
		currency,
		locale,
		formatValue,
		getIcon,
		timeFormat24: workspace?.profile?.timeFormat24,
	};

	// An extra field replaces the auto-resolved field with the same id.
	const extraFieldIds = new Set(extraFields.map((field) => field.id));
	const availableFields = fields.filter((field) => !extraFieldIds.has(field.id));

	return (
		<Fragment>
			{extraFields.map((field) => (
				<PlainFieldRenderer key={`extra-${field.id}`} field={field} {...options} />
			))}
			{availableFields.map((field, index) => {
				const override = overrides.get(field.id);
				return (
					<Fragment key={`${field.id}-${index}`}>
						{override?.renderValue ? override.renderValue(field) : renderField(field, options)}
					</Fragment>
				);
			})}
		</Fragment>
	);
};
